import {
  ExerciseBenchmark,
  getExerciseDisplay,
  getMetricSpec,
  getSelectedExerciseId,
  HasWorkoutLayer,
  MaxConversion,
  mergeWorkoutLayers,
  mergeWorkoutLayersWithExternal,
  WorkoutAlternateExercise,
  WorkoutAlternateGroup,
  WorkoutExercise,
  WorkoutExerciseMetric,
  WorkoutSet,
  WorkoutSuperset,
  WorkoutSupersetExercise,
} from '@strength/domain-workout';
import {
  AthletePublishedWorkoutLayer,
  ExerciseOption,
  MergedAthleteWorkout,
  MetricOption,
} from './types';
import { ActualsSetFilter } from './set-select';
import { Metric } from '@strength/domain';
import { RecordType } from '@strength/db-schema';
import type { I18nTranslator } from '@strength/i18n';

export const tabletAvatarStyle = {
  height: 32,
  width: 32,
  fontSize: 16,
};

/**
 * Placeholder rows (SC-4258/SC-4299) exist only so AMS-ingested actuals have
 * somewhere to attach on a bowling day the athlete was never prescribed.
 * Coaches see them in Bowling Loads; every athlete actuals-recording surface
 * (bulk actuals, tablet mode) must exclude them.
 */
export const excludePlaceholderLayers = <
  T extends { recordType?: RecordType | null },
>(
  layers: T[],
): T[] => layers.filter((layer) => !layer.recordType);

export const getExercisesFromEntry = (
  entry: WorkoutExercise | WorkoutSuperset | WorkoutAlternateGroup,
  actualLayer: HasWorkoutLayer | null,
) => {
  switch (entry.__type) {
    case 'WorkoutSuperset': {
      return entry.exercises.map(
        ({ exercise }: WorkoutSupersetExercise) => exercise,
      );
    }
    case 'WorkoutAlternateGroup': {
      const selectedExerciseId = getSelectedExerciseId(entry, actualLayer);
      const selectedExercise = entry.exercises.find(
        (e: WorkoutAlternateExercise) =>
          e.workoutExerciseId === selectedExerciseId,
      )?.exercise;
      return selectedExercise ? [selectedExercise] : [];
    }
    case 'WorkoutExercise':
      return [entry];

    default:
      return [];
  }
};

export const getMetricsForExerciseDisplay = (exercise: WorkoutExercise) => {
  const metrics: WorkoutExerciseMetric[] = [];
  exercise.metrics.forEach((m) => {
    if (!metrics.find((a) => a.metricId === m.metricId)) {
      metrics.push(m);
    }
  });
  return metrics;
};

export const getOriginalSet = (
  workoutSetId: string,
  originalExercise?: WorkoutExercise,
) => {
  if (!originalExercise) return undefined;
  return originalExercise.sets.find((s) => s.workoutSetId === workoutSetId);
};

export const getEntryKey = (
  entry: WorkoutExercise | WorkoutSuperset | WorkoutAlternateGroup,
) => {
  if (entry.__type === 'WorkoutAlternateGroup')
    return entry.workoutAlternateGroupId;
  if (entry.__type === 'WorkoutSuperset') return entry.workoutSupersetId;
  return entry.workoutExerciseId;
};

/**
 * Whether to display a set based on the set filter
 */
export const shouldDisplaySet = (
  set: WorkoutSet,
  setIndex: number,
  filter: ActualsSetFilter,
  numSets: number,
) => {
  if (filter === 'sets-for-collection') {
    return set.metrics.some((m) => m.forCollection);
  }
  if (filter === 'last-sets') {
    return setIndex === numSets - 1;
  }
  return true;
};

/**
 * Source ranking for the bulk actuals page merge, aligned with
 * `ACTUALS_SOURCE_RANKING` on the bowling block summary:
 * - 'Manual Override' first: bulk-page edits are practitioner-entered and must
 *   keep beating a Catapult / Player Entered sync that lands afterwards.
 * - Feeds above null: athlete-typed actuals (mobile, tablet) and legacy
 *   un-stamped entries lose to Catapult / Player Entered, same as the
 *   bowling summary.
 * - 'Prescription' is omitted: this merge does not tag prescription layers
 *   with the sentinel, so their values are null-ranked and lose to
 *   actual-layer sources; same-rank ties with actual-layer nulls still
 *   resolve by layer order (actuals override prescriptions).
 */
export const BULK_ACTUALS_SOURCE_RANKING = [
  'Manual Override',
  'Catapult',
  'Player Entered',
  null,
  'Match Stats',
] as const;

export const getAthleteWorkoutsForLayers = (
  athleteWorkouts: AthletePublishedWorkoutLayer[],
  exerciseBenchmarks: ExerciseBenchmark[],
  conversions: MaxConversion[],
): MergedAthleteWorkout[] => {
  return athleteWorkouts.map((athleteLayer) => {
    const {
      profileId,
      blockId,
      actual,
      version,
      blockVersion,
      blockLayer,
      assignedDate,
      modifier,
      status,
      block,
    } = athleteLayer;

    const baseWorkout = blockLayer ?? athleteLayer;
    const athleteWorkout = blockLayer ? athleteLayer : undefined;

    return {
      context: {
        profileId,
        blockId,
        athleteVersion: version,
        blockVersion,
        assignedDate: assignedDate || Date.now().toString(),
        blockLayerId: blockLayer?.id,
        athleteLayerId: athleteLayer.id,
        actualLayerId: actual?.id ?? '',
        workoutStatus: status,
      },
      workout: mergeWorkoutLayersWithExternal(
        BULK_ACTUALS_SOURCE_RANKING,
        baseWorkout,
        athleteWorkout,
        modifier,
        actual,
      ),
      originalWorkout: mergeWorkoutLayers(
        baseWorkout,
        athleteWorkout,
        modifier,
      ),
      exerciseBenchmarks,
      conversions,
      trainingMaxRatio: block?.trainingMaxRatio ?? 1,
    };
  });
};

export const getOptionsFromWorkouts = (
  mergedAthleteWorkouts: Pick<MergedAthleteWorkout, 'workout'>[],
  t: I18nTranslator,
): {
  exerciseOptions: ExerciseOption[];
  metricOptions: MetricOption[];
} => {
  const exerciseOptions: ExerciseOption[] = [];
  const metricOptions: MetricOption[] = [];
  const uniqueWorkoutExerciseIds = new Set<string>();
  const uniqueMetricIds = new Set<string>();

  mergedAthleteWorkouts.forEach(({ workout }) =>
    workout.sections.forEach((section) =>
      section.entries.forEach((entry) => {
        if (entry.__type === 'WorkoutExercise') {
          // Add exercise option
          if (!uniqueWorkoutExerciseIds.has(entry.workoutExerciseId)) {
            uniqueWorkoutExerciseIds.add(entry.workoutExerciseId);
            exerciseOptions.push({
              workoutExerciseId: entry.workoutExerciseId,
              libraryExerciseId: entry.libraryExerciseId,
              name: getExerciseDisplay(entry, t),
              alternates: [],
              isAlternate: false,
            });
          }
          // Add metric options
          entry.metrics.forEach((m) => {
            if (!uniqueMetricIds.has(m.metricId)) {
              uniqueMetricIds.add(m.metricId);
              metricOptions.push({ id: m.metricId, name: m.metricName });
            }
          });
        } else {
          entry.exercises.forEach((e) => {
            // Add exercise option
            if (!uniqueWorkoutExerciseIds.has(e.exercise.workoutExerciseId)) {
              uniqueWorkoutExerciseIds.add(e.exercise.workoutExerciseId);

              if (
                entry.__type === 'WorkoutSuperset' ||
                (entry.__type === 'WorkoutAlternateGroup' && e.sortOrder === 0)
              ) {
                exerciseOptions.push({
                  workoutExerciseId: e.exercise.workoutExerciseId,
                  libraryExerciseId: e.exercise.libraryExerciseId,
                  name: getExerciseDisplay(e.exercise, t),
                  isAlternate: false,
                  alternates:
                    entry.__type === 'WorkoutAlternateGroup'
                      ? entry.exercises
                          .filter((ex) => ex.sortOrder !== 0)
                          .map((alt) => ({
                            workoutExerciseId: alt.exercise.workoutExerciseId,
                            libraryExerciseId: alt.exercise.libraryExerciseId,
                            name: getExerciseDisplay(alt.exercise, t),
                            isAlternate: true,
                          }))
                      : [],
                });
              }
            }
            // Add metric options
            e.exercise.metrics.forEach((m) => {
              if (!uniqueMetricIds.has(m.metricId)) {
                uniqueMetricIds.add(m.metricId);
                metricOptions.push({
                  id: m.metricId,
                  name: m.metricName,
                });
              }
            });
          });
        }
      }),
    ),
  );

  return { exerciseOptions, metricOptions };
};

export const getUniqueExerciseOptions = (
  exerciseOptions: { libraryExerciseId: string; name: string }[],
) => {
  return exerciseOptions.reduce(
    (list, next) => {
      const { libraryExerciseId, name } = next;
      if (!list.some((e) => e.id === libraryExerciseId)) {
        list.push({ id: libraryExerciseId, name });
      }
      return list;
    },
    [] as { id: string; name: string }[],
  );
};

export function expandedExerciseOptions(exerciseOptions: ExerciseOption[]) {
  return exerciseOptions.flatMap((e) => {
    return [e, ...e.alternates];
  });
}

// Get the most relevant unit to apply to an actuals input
// This is used when an actual value is entered without a unit for us to
// auto populate it
// Note: we pass in the units instead of using metric.config.units because this
// allows the caller to use `getUnitsForMetric`, which handles the `defaultWeightUnit`
export function detectActualsUnit(
  prescribed: string | undefined,
  metric: Metric | undefined,
  units: string[] | undefined,
): string {
  if (!metric) {
    return '';
  }

  if (prescribed) {
    const spec = getMetricSpec(metric);
    const parsed = spec.schema.safeParse(prescribed);

    if (parsed.success && parsed.data !== '') {
      const data = parsed.data;

      if (metric.type === 'intensity' && typeof data === 'object') {
        if (data.format === 'weight') {
          return data.unit;
        }
      } else if (typeof data === 'object' && 'unit' in data) {
        return data.unit;
      }
    }
  }

  return units?.[0] ?? '';
}
